import { useAppSelector } from '@hooks/app/redux';
import { getNextPageParam } from '@lib/utils/reactQuery/infinityQueryHelpers';
import { getPatientPrescriptionsRequests } from '@services/external/e-prescriptions.api';
import React from 'react';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { GetPrescriptionsParams } from 'types/e-prescription.types';

const PAGE_SIZE = 20;

type QParams = Omit<GetPrescriptionsParams, 'page' | 'pageSize' | 'clientId'> & {
  providerId: string;
  clientId?: string;
};

async function handler(page: number = 1, params: QParams) {
  if (!params.clientId) {
    throw new Error('No client id');
  }

  const resData = await getPatientPrescriptionsRequests(params.providerId, {
    page,
    pageSize: PAGE_SIZE,
    clientId: params.clientId,
    ...params,
  });

  return resData.data.data;
}

type TQueryData = Awaited<ReturnType<typeof handler>>;

function usePrescriptionsRequest(
  clientId?: string,
  params: Omit<QParams, 'providerId' | 'clientId'> = {
    direction: 'DESC',
    sortBy: 'createdAt',
  },
  options?: Omit<UseInfiniteQueryOptions<TQueryData, any, TQueryData, TQueryData, any>, 'queryKey'>,
) {
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const { data, ...rest } = useInfiniteQuery(
    ['prescriptions:prescriptions-requests', params, providerId, clientId],
    ({ pageParam }) => handler(pageParam, { ...params, providerId, clientId }),
    {
      ...options,
      getNextPageParam: getNextPageParam,
      enabled: (options?.enabled ?? true) && !!clientId,
    },
  );

  const flattenData = React.useMemo(() => {
    return data?.pages.flatMap((p) => p.items) ?? [];
  }, [data]);

  return { data: flattenData, original: data, ...rest };
}

export default usePrescriptionsRequest;
